import type {
  AppointmentResource,
  AppointmentTypeResource,
  AppointmentTypesResource,
  AttachAppointmentBody,
  CreateAppointmentTypeBody,
  OpenStageValue,
  UnmatchedAppointmentsResource,
  UpdateAppointmentTypeBody,
} from '@majuscule/contracts';
import { openStageSchema } from '@majuscule/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarPlus, Copy } from 'lucide-react';
import { type JSX, useState } from 'react';
import { useCompanies, useCompany } from '../components/crm/shared';
import { internalFetch, internalSend } from '../lib/api';
import { CRM_KEY, errorMessage, formatInstant, OPEN_STAGES, STAGE_LABEL } from '../lib/crm';
import { useToast } from '@/components/toast';
import { PageChrome } from '@/components/page-chrome';
import { Callout } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHead } from '@/components/ui/card';
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHead } from '@/components/ui/dialog';
import { EmptyState, LoadingState } from '@/components/ui/empty';
import { CheckRow, Field, FieldHint } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/native-select';
import { PageHead } from '@/components/ui/page-header';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

const TYPES_KEY = [...CRM_KEY, 'appointment-types'];
const UNMATCHED_KEY = [...CRM_KEY, 'appointments-unmatched'];

/**
 * Configuration des rendez-vous (lot 22, administrateurs) : types de
 * rendez-vous (R1, R2, Démo…) reliés aux types d'événement Cal.com, URL du
 * webhook à déclarer dans Cal.com, réservations à rattacher.
 */
export function AppointmentTypesPage(): JSX.Element {
  const qc = useQueryClient();
  const { notify } = useToast();
  const [editing, setEditing] = useState<AppointmentTypeResource | 'new' | null>(null);
  const [attaching, setAttaching] = useState<AppointmentResource | null>(null);
  const types = useQuery({
    queryKey: TYPES_KEY,
    queryFn: () => internalFetch<AppointmentTypesResource>('/internal/crm/appointment-types'),
  });
  const unmatched = useQuery({
    queryKey: UNMATCHED_KEY,
    queryFn: () => internalFetch<UnmatchedAppointmentsResource>('/internal/crm/appointments/unmatched'),
  });
  const refresh = (): void => {
    void qc.invalidateQueries({ queryKey: CRM_KEY });
  };
  const data = types.data;

  return (
    <>
      <PageChrome
        crumbs={[{ label: 'Rendez-vous' }]}
        actions={
          <Button
            type="button"
            onClick={() => {
              setEditing('new');
            }}
          >
            <CalendarPlus aria-hidden="true" />
            Nouveau type
          </Button>
        }
      />
      <PageHead
        title="Rendez-vous"
        subtitle="Types de rendez-vous Cal.com : la fiche entreprise ou affaire ouvre leur page de réservation, et le webhook rapporte les dates"
      />

      {data !== undefined ? (
        <Card>
          <CardHead title="Webhook Cal.com" description="Cal.com → Settings → Developer → Webhooks" />
          <CardContent className="grid gap-3">
            {data.webhookConfigured ? null : (
              <Callout variant="warning">
                Secret absent : définir CALCOM_WEBHOOK_SECRET sur l’API (le même que dans Cal.com). Le
                webhook répond 503 en attendant.
              </Callout>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <code className="rounded-md bg-muted px-2 py-1 font-mono text-[13px]">{data.webhookUrl}</code>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                aria-label="Copier l’URL du webhook"
                onClick={() => {
                  void navigator.clipboard.writeText(data.webhookUrl).then(() => {
                    notify('URL copiée');
                  });
                }}
              >
                <Copy aria-hidden="true" />
              </Button>
            </div>
            <p className="m-0 text-caption text-muted-foreground">
              Déclencheurs : Booking Created, Booking Rescheduled, Booking Cancelled. Secret : la valeur de
              CALCOM_WEBHOOK_SECRET.
            </p>
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHead title="Types de rendez-vous" className="pb-4" />
        {types.isLoading ? (
          <LoadingState className="border-t border-border" />
        ) : (data?.types.length ?? 0) === 0 ? (
          <EmptyState
            className="border-t border-border"
            title="Aucun type de rendez-vous"
            description="Créer R1, R2, Démo… avec le lien de réservation Cal.com de chacun."
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Type</TableHead>
                <TableHead>Page de réservation</TableHead>
                <TableHead>Étape à la réservation</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead kind="act">
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(data?.types ?? []).map((type) => (
                <TableRow key={type.id}>
                  <TableCell kind="name">{type.name}</TableCell>
                  <TableCell kind="mono">
                    {type.bookingUrl}
                    {type.calEventSlug !== null ? (
                      <span className="block font-sans text-caption text-muted-foreground">
                        Slug Cal.com : {type.calEventSlug}
                      </span>
                    ) : null}
                  </TableCell>
                  <TableCell kind="sub">
                    {type.stageOnBooking === null ? '—' : STAGE_LABEL[type.stageOnBooking]}
                  </TableCell>
                  <TableCell>
                    {type.active ? <Badge variant="success">Actif</Badge> : <Badge variant="neutral">Désactivé</Badge>}
                  </TableCell>
                  <TableCell kind="act">
                    <Button
                      type="button"
                      variant="outline"
                      size="xs"
                      onClick={() => {
                        setEditing(type);
                      }}
                    >
                      Modifier
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <Card>
        <CardHead
          title="Réservations à rattacher"
          description="Ni l’entreprise du lien ni l’e-mail de l’invité n’ont permis de retrouver la fiche"
          className="pb-4"
        />
        {(unmatched.data?.appointments.length ?? 0) === 0 ? (
          <EmptyState className="border-t border-border" title="Tout est rattaché" />
        ) : (
          <Table>
            <TableBody>
              {(unmatched.data?.appointments ?? []).map((appointment) => (
                <TableRow key={appointment.id}>
                  <TableCell kind="mono" className="w-[190px]">
                    {formatInstant(appointment.startAt)}
                  </TableCell>
                  <TableCell kind="name">
                    {appointment.typeName ?? appointment.title}
                    <span className="block text-caption text-muted-foreground">
                      {appointment.attendeeName ?? '—'} · {appointment.attendeeEmail ?? '—'}
                      {appointment.status === 'cancelled' ? ' · annulé' : ''}
                    </span>
                  </TableCell>
                  <TableCell kind="act">
                    <Button
                      type="button"
                      variant="outline"
                      size="xs"
                      onClick={() => {
                        setAttaching(appointment);
                      }}
                    >
                      Rattacher
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <Dialog
        open={editing !== null}
        onOpenChange={(next) => {
          if (!next) setEditing(null);
        }}
      >
        {editing !== null ? (
          <TypeForm
            type={editing === 'new' ? null : editing}
            onDone={() => {
              refresh();
              setEditing(null);
            }}
            onClose={() => {
              setEditing(null);
            }}
          />
        ) : null}
      </Dialog>
      <Dialog
        open={attaching !== null}
        onOpenChange={(next) => {
          if (!next) setAttaching(null);
        }}
      >
        {attaching !== null ? (
          <AttachForm
            appointment={attaching}
            onDone={() => {
              refresh();
              setAttaching(null);
              notify('Rendez-vous rattaché');
            }}
            onClose={() => {
              setAttaching(null);
            }}
          />
        ) : null}
      </Dialog>
    </>
  );
}

function TypeForm({
  type,
  onDone,
  onClose,
}: {
  type: AppointmentTypeResource | null;
  onDone: () => void;
  onClose: () => void;
}): JSX.Element {
  const [name, setName] = useState(type?.name ?? '');
  const [bookingUrl, setBookingUrl] = useState(type?.bookingUrl ?? '');
  const [slug, setSlug] = useState(type?.calEventSlug ?? '');
  const [stage, setStage] = useState<OpenStageValue | ''>(type?.stageOnBooking ?? '');
  const [active, setActive] = useState(type?.active ?? true);
  const save = useMutation({
    mutationFn: () => {
      const fields = {
        name: name.trim(),
        bookingUrl: bookingUrl.trim(),
        calEventSlug: slug.trim() === '' ? null : slug.trim(),
        stageOnBooking: stage === '' ? null : stage,
        active,
      };
      if (type === null) {
        const body: CreateAppointmentTypeBody = fields;
        return internalSend<AppointmentTypeResource>('POST', '/internal/crm/appointment-types', body);
      }
      const body: UpdateAppointmentTypeBody = fields;
      return internalSend<AppointmentTypeResource>('PATCH', `/internal/crm/appointment-types/${type.id}`, body);
    },
    onSuccess: onDone,
  });
  return (
    <DialogContent aria-describedby={undefined}>
      <DialogHead title={type === null ? 'Nouveau type de rendez-vous' : `Modifier ${type.name}`} />
      <form
        id="appointment-type-form"
        className="contents"
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate();
        }}
      >
        <DialogBody>
          <Field label="Nom" required>
            <Input
              required
              maxLength={50}
              placeholder="R1, R2, Démo…"
              value={name}
              onChange={(event) => {
                setName(event.target.value);
              }}
            />
          </Field>
          <Field label="Page de réservation Cal.com" required>
            <Input
              required
              type="url"
              placeholder="https://cal.com/julien/r1"
              value={bookingUrl}
              onChange={(event) => {
                setBookingUrl(event.target.value);
              }}
            />
            <FieldHint>Le lien public du type d’événement, sans paramètres.</FieldHint>
          </Field>
          <Field label="Slug du type d’événement">
            <Input
              placeholder="Par défaut : la fin du lien (r1)"
              value={slug}
              onChange={(event) => {
                setSlug(event.target.value);
              }}
            />
            <FieldHint>Sert à reconnaître le type dans le webhook.</FieldHint>
          </Field>
          <Field label="Étape de l’affaire à la réservation">
            <NativeSelect
              value={stage}
              onChange={(event) => {
                const parsed = openStageSchema.safeParse(event.target.value);
                setStage(parsed.success ? parsed.data : '');
              }}
            >
              <option value="">Aucune</option>
              {OPEN_STAGES.map((value) => (
                <option key={value} value={value}>
                  {STAGE_LABEL[value]}
                </option>
              ))}
            </NativeSelect>
            <FieldHint>Une affaire ouverte moins avancée y passe quand un tel rendez-vous est pris.</FieldHint>
          </Field>
          <CheckRow
            label="Actif (proposé dans « Planifier »)"
            checked={active}
            onChange={(event) => {
              setActive(event.target.checked);
            }}
          />
          {save.isError ? <Callout variant="destructive">{errorMessage(save.error, 'Enregistrement refusé.')}</Callout> : null}
        </DialogBody>
      </form>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          Annuler
        </Button>
        <Button type="submit" form="appointment-type-form" size="lg" disabled={save.isPending}>
          Enregistrer
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

function AttachForm({
  appointment,
  onDone,
  onClose,
}: {
  appointment: AppointmentResource;
  onDone: () => void;
  onClose: () => void;
}): JSX.Element {
  const companies = useCompanies();
  const [companyId, setCompanyId] = useState('');
  const company = useCompany(companyId === '' ? null : companyId);
  const [dealId, setDealId] = useState('');
  const attach = useMutation({
    mutationFn: () => {
      const body: AttachAppointmentBody = { companyId, dealId: dealId === '' ? null : dealId };
      return internalSend<AppointmentResource>('POST', `/internal/crm/appointments/${appointment.id}/attach`, body);
    },
    onSuccess: onDone,
  });
  return (
    <DialogContent aria-describedby={undefined}>
      <DialogHead
        title="Rattacher le rendez-vous"
        description={`${appointment.typeName ?? appointment.title}, ${formatInstant(appointment.startAt)}`}
      />
      <DialogBody>
        <Field label="Entreprise" required>
          <NativeSelect
            value={companyId}
            onChange={(event) => {
              setCompanyId(event.target.value);
              setDealId('');
            }}
          >
            <option value="">Choisir…</option>
            {(companies.data?.companies ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </NativeSelect>
        </Field>
        <Field label="Affaire">
          <NativeSelect
            value={dealId}
            disabled={company.data === undefined}
            onChange={(event) => {
              setDealId(event.target.value);
            }}
          >
            <option value="">Aucune (rendez-vous de l’entreprise)</option>
            {(company.data?.deals ?? []).map((d) => (
              <option key={d.id} value={d.id}>
                {d.title}
              </option>
            ))}
          </NativeSelect>
        </Field>
        {attach.isError ? <Callout variant="destructive">{errorMessage(attach.error, 'Rattachement refusé.')}</Callout> : null}
      </DialogBody>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          Annuler
        </Button>
        <Button
          type="button"
          size="lg"
          disabled={attach.isPending || companyId === ''}
          onClick={() => {
            attach.mutate();
          }}
        >
          Rattacher
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
